import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { RequireAdmin } from './app/guards/require-admin';
import { RequireParticipant } from './app/guards/require-participant';
import { LoadingState } from './components/app/page-states';

const OnboardingPage = lazy(() =>
  import('./pages/onboarding-page').then((module) => ({ default: module.OnboardingPage })),
);
const ResumeParticipantPage = lazy(() =>
  import('./pages/resume-participant-page').then((module) => ({
    default: module.ResumeParticipantPage,
  })),
);
const MainLayout = lazy(() =>
  import('./app/layouts/main-layout').then((module) => ({ default: module.MainLayout })),
);
const DashboardPage = lazy(() =>
  import('./pages/dashboard-page').then((module) => ({ default: module.DashboardPage })),
);
const ToolQuestionnairePage = lazy(() =>
  import('./pages/tool-questionnaire-page').then((module) => ({
    default: module.ToolQuestionnairePage,
  })),
);
const AdminLoginPage = lazy(() =>
  import('./pages/admin/admin-login-page').then((module) => ({
    default: module.AdminLoginPage,
  })),
);
const AdminLayout = lazy(() =>
  import('./pages/admin/admin-layout').then((module) => ({ default: module.AdminLayout })),
);
const AdminDashboardPage = lazy(() =>
  import('./pages/admin/admin-dashboard-page').then((module) => ({
    default: module.AdminDashboardPage,
  })),
);
const AdminParticipantsPage = lazy(() =>
  import('./pages/admin/admin-participants-page').then((module) => ({
    default: module.AdminParticipantsPage,
  })),
);
const AdminParticipantDetailsPage = lazy(() =>
  import('./pages/admin/admin-participant-details-page').then((module) => ({
    default: module.AdminParticipantDetailsPage,
  })),
);
const AdminToolsPage = lazy(() =>
  import('./pages/admin/admin-tools-page').then((module) => ({
    default: module.AdminToolsPage,
  })),
);
const NotFoundPage = lazy(() =>
  import('./pages/not-found-page').then((module) => ({ default: module.NotFoundPage })),
);

function withFallback(element: ReactNode) {
  return <Suspense fallback={<LoadingState lines={3} />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: withFallback(<OnboardingPage />),
  },
  {
    path: '/resume',
    element: withFallback(<ResumeParticipantPage />),
  },
  {
    path: '/dashboard',
    element: <RequireParticipant />,
    children: [
      {
        element: withFallback(<MainLayout />),
        children: [
          {
            index: true,
            element: withFallback(<DashboardPage />),
          },
          {
            path: 'tools/:toolSlug',
            element: withFallback(<ToolQuestionnairePage />),
          },
        ],
      },
    ],
  },
  {
    path: '/admin/login',
    element: withFallback(<AdminLoginPage />),
  },
  {
    path: '/admin',
    element: <RequireAdmin />,
    children: [
      {
        element: withFallback(<AdminLayout />),
        children: [
          {
            index: true,
            element: withFallback(<AdminDashboardPage />),
          },
          {
            path: 'participants',
            element: withFallback(<AdminParticipantsPage />),
          },
          {
            path: 'participants/:id',
            element: withFallback(<AdminParticipantDetailsPage />),
          },
          {
            path: 'tools',
            element: withFallback(<AdminToolsPage />),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: withFallback(<NotFoundPage />),
  },
]);
